import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, ChefHat, User, UtensilsCrossed, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Seo } from "@/lib/seo";
import { COUNTRIES, PRIORITY, formatPhone } from "@/lib/countries";

const OCCASIONS = ["Dîner romantique", "Anniversaire", "Dîner entre amis", "Repas de famille", "Réception privée ou VIP", "Dîner professionnel", "Autre"];
const CUISINES = ["Africaine", "Saveurs des îles", "Française", "Mixte", "Je souhaite être conseillée"];
const FORMATS = ["Menu servi à table", "Dîner en plusieurs temps", "Repas convivial", "Formule personnalisée", "Je souhaite être conseillée"];
const SERVICES = ["Mise en place", "Service à table", "Dressage", "Gestion de la vaisselle", "Je souhaite en discuter", "Aucune"];
const STEPS = [{ label: "Coordonnées", icon: User }, { label: "Le projet", icon: UtensilsCrossed }, { label: "Envoi", icon: Send }];
const PRIO = PRIORITY.map((c) => COUNTRIES.find((x) => x.code === c)).filter(Boolean);

const INIT = {
  first_name: "", last_name: "", email: "", phone_country: "FR", phone_number: "",
  occasion: "", occasion_other: "", event_date: "", city: "", country: "France", guests: "",
  cuisine: "", meal_format: "", budget: "", services: [], message: "", consent: false, website: "",
};

const inputCls = "w-full rounded-xl border border-[#2C2C2C]/15 bg-white px-4 py-3 text-base text-[#2C2C2C] outline-none transition-colors focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20";
const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

function Field({ label, error, children, optional, group }) {
  const Tag = group ? "div" : "label";
  return (
    <Tag className="block" role={group ? "group" : undefined}>
      <span className="mb-1.5 block text-sm font-medium text-[#2C2C2C]">{label}{!optional && <span className="text-[#C9A84C]"> *</span>}{optional && <span className="font-normal text-[#2C2C2C]/50"> (facultatif)</span>}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600" data-testid="field-error">{error}</span>}
    </Tag>
  );
}

function Chips({ options, value, onChange, multi, testId, error }) {
  const isOn = (o) => (multi ? value.includes(o) : value === o);
  return (
    <div>
      <div className="flex flex-wrap gap-2.5" data-testid={testId}>
        {options.map((o, i) => (
          <button type="button" key={o} onClick={() => onChange(o)} data-testid={`${testId}-${i}`} aria-pressed={isOn(o)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2.5 text-sm transition-colors ${isOn(o) ? "border-[#2C2C2C] bg-[#2C2C2C] text-white" : "border-[#2C2C2C]/15 bg-white text-[#2C2C2C] hover:border-[#C9A84C]"}`}>
            {multi && isOn(o) && <Check size={14} className="text-[#C9A84C]" />} {o}
          </button>
        ))}
      </div>
      {error && <span className="mt-1.5 block text-xs text-red-600" data-testid="field-error">{error}</span>}
    </div>
  );
}

function Stepper({ step }) {
  return (
    <ol className="mb-10 flex items-center gap-2 sm:gap-4" data-testid="chef-stepper">
      {STEPS.map((s, i) => (
        <li key={s.label} className="flex flex-1 items-center gap-2 sm:gap-3">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm transition-colors ${i < step ? "border-[#C9A84C] bg-[#C9A84C] text-white" : i === step ? "border-[#2C2C2C] bg-[#2C2C2C] text-white" : "border-[#2C2C2C]/20 text-[#2C2C2C]/40"}`}>
            {i < step ? <Check size={16} /> : <s.icon size={15} />}
          </span>
          <span className={`hidden text-sm sm:block ${i === step ? "font-medium text-[#2C2C2C]" : "text-[#2C2C2C]/50"}`}>{s.label}</span>
          {i < STEPS.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-[#C9A84C]" : "bg-[#2C2C2C]/15"}`} />}
        </li>
      ))}
    </ol>
  );
}

function StepContact({ f, set, err }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2" data-testid="chef-step-1">
      <Field label="Nom" error={err.last_name}><input className={inputCls} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} autoComplete="family-name" data-testid="chef-last-name" /></Field>
      <Field label="Prénom" error={err.first_name}><input className={inputCls} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} autoComplete="given-name" data-testid="chef-first-name" /></Field>
      <div className="sm:col-span-2"><Field label="E-mail" error={err.email}><input type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" data-testid="chef-email" /></Field></div>
      <div className="sm:col-span-2">
        <Field group label="Téléphone" error={err.phone}>
          <div className="flex gap-2">
            <select className={`${inputCls} w-[44%] sm:w-56`} value={f.phone_country} onChange={(e) => set("phone_country", e.target.value)} data-testid="chef-phone-country" aria-label="Indicatif">
              {PRIO.map((c) => <option key={`p-${c.code}`} value={c.code}>{c.name} (+{c.dial})</option>)}
              <option disabled>──────────</option>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name} (+{c.dial})</option>)}
            </select>
            <input type="tel" className={inputCls} value={f.phone_number} onChange={(e) => set("phone_number", e.target.value)} autoComplete="tel-national" placeholder="6 12 34 56 78" data-testid="chef-phone" />
          </div>
        </Field>
      </div>
    </div>
  );
}

function StepProject({ f, set, err, toggleService }) {
  return (
    <div className="space-y-7" data-testid="chef-step-2">
      <Field group label="Occasion" error={err.occasion}><Chips options={OCCASIONS} value={f.occasion} onChange={(v) => set("occasion", v)} testId="chef-occasion" /></Field>
      {f.occasion === "Autre" && <Field label="Précisez l'occasion" optional><input className={inputCls} value={f.occasion_other} onChange={(e) => set("occasion_other", e.target.value)} data-testid="chef-occasion-other" /></Field>}
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Date de la prestation" error={err.event_date}><input type="date" min={today} className={inputCls} value={f.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="chef-date" /></Field>
        <Field label="Nombre de convives" error={err.guests}><input type="number" min="1" max="500" inputMode="numeric" className={inputCls} value={f.guests} onChange={(e) => set("guests", e.target.value)} data-testid="chef-guests" /></Field>
        <Field label="Ville" error={err.city}><input className={inputCls} value={f.city} onChange={(e) => set("city", e.target.value)} autoComplete="address-level2" data-testid="chef-city" /></Field>
        <Field label="Pays" error={err.country}>
          <select className={inputCls} value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="chef-country">
            <option value="">Choisir un pays</option>
            {PRIO.map((c) => <option key={`p-${c.code}`} value={c.name}>{c.name}</option>)}
            <option disabled>──────────</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
          </select>
        </Field>
      </div>
      <Field group label="Type de cuisine" error={err.cuisine}><Chips options={CUISINES} value={f.cuisine} onChange={(v) => set("cuisine", v)} testId="chef-cuisine" /></Field>
      <Field group label="Format du repas" error={err.meal_format}><Chips options={FORMATS} value={f.meal_format} onChange={(v) => set("meal_format", v)} testId="chef-format" /></Field>
      <Field label="Budget envisagé" error={err.budget}><input className={inputCls} value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder="Indiquez votre budget ou une fourchette" data-testid="chef-budget" /></Field>
      <Field group label="Prestations complémentaires" error={err.services}><Chips multi options={SERVICES} value={f.services} onChange={toggleService} testId="chef-services" /></Field>
      <Field label="Précisions supplémentaires" optional><textarea rows={4} className={inputCls} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Allergies, régime alimentaire, ambiance souhaitée, contraintes du lieu…" data-testid="chef-message" /></Field>
    </div>
  );
}

function Summary({ f }) {
  const rows = [
    ["Nom", `${f.first_name} ${f.last_name}`], ["E-mail", f.email], ["Téléphone", formatPhone(f.phone_country, f.phone_number)],
    ["Occasion", f.occasion === "Autre" && f.occasion_other ? `Autre : ${f.occasion_other}` : f.occasion], ["Date", f.event_date && new Date(`${f.event_date}T12:00:00`).toLocaleDateString("fr-FR")],
    ["Lieu", `${f.city}, ${f.country}`], ["Convives", f.guests], ["Cuisine", f.cuisine], ["Format", f.meal_format], ["Budget", f.budget],
    ["Prestations", f.services.join(", ")], ["Précisions", f.message],
  ];
  return (
    <dl className="divide-y divide-[#2C2C2C]/10 rounded-2xl border border-[#C9A84C]/30 bg-[#FAF6EF] px-5" data-testid="chef-summary">
      {rows.filter(([, v]) => v).map(([k, v]) => (
        <div key={k} className="grid grid-cols-3 gap-3 py-3 text-sm"><dt className="text-[#2C2C2C]/55">{k}</dt><dd className="col-span-2 break-words text-[#2C2C2C]">{v}</dd></div>
      ))}
    </dl>
  );
}

function validate(step, f) {
  const e = {};
  if (step === 0) {
    if (!f.last_name.trim()) e.last_name = "Indiquez votre nom";
    if (!f.first_name.trim()) e.first_name = "Indiquez votre prénom";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Adresse e-mail invalide";
    if (!formatPhone(f.phone_country, f.phone_number)) e.phone = "Numéro invalide pour l'indicatif choisi";
  }
  if (step === 1) {
    if (!f.occasion) e.occasion = "Choisissez une occasion";
    if (!f.event_date) e.event_date = "Choisissez une date";
    else if (f.event_date < today) e.event_date = "La date doit être à venir";
    if (!f.city.trim()) e.city = "Indiquez la ville";
    if (!f.country) e.country = "Choisissez le pays";
    if (!(Number(f.guests) >= 1 && Number(f.guests) <= 500)) e.guests = "Entre 1 et 500 convives";
    if (!f.cuisine) e.cuisine = "Choisissez un type de cuisine";
    if (!f.meal_format) e.meal_format = "Choisissez un format";
    if (!f.budget.trim()) e.budget = "Indiquez votre budget";
    if (!f.services.length) e.services = "Choisissez au moins une option";
  }
  if (step === 2 && !f.consent) e.consent = "Votre accord est nécessaire pour traiter la demande";
  return e;
}

function Success({ reference }) {
  return (
    <div className="py-10 text-center" data-testid="chef-success">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#C9A84C] text-white"><Check size={30} /></div>
      <h2 className="mt-6 font-serif text-3xl text-[#2C2C2C] lg:text-4xl">Merci, votre demande est bien enregistrée</h2>
      <p className="mx-auto mt-4 max-w-md text-base text-[#2C2C2C]/70">Nous revenons vers vous rapidement pour imaginer ensemble votre dîner privé.</p>
      <p className="mt-6 text-sm text-[#2C2C2C]/60">Référence de votre demande : <strong className="text-[#2C2C2C]" data-testid="chef-success-reference">{reference}</strong></p>
      <Link to="/cheffe-a-domicile" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#9A7B2C] hover:text-[#2C2C2C]" data-testid="chef-success-back"><ArrowLeft size={15} /> Retour à la page Cheffe à domicile</Link>
    </div>
  );
}

export default function DevisCheffe() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INIT);
  const [err, setErr] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [reference, setReference] = useState("");

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setErr((p) => ({ ...p, [k]: undefined })); };
  const toggleService = (s) => {
    setErr((p) => ({ ...p, services: undefined }));
    setF((p) => {
      if (s === "Aucune") return { ...p, services: p.services.includes("Aucune") ? [] : ["Aucune"] };
      const cur = p.services.filter((x) => x !== "Aucune");
      return { ...p, services: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] };
    });
  };
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const next = () => {
    const e = validate(step, f);
    setErr(e);
    if (Object.keys(e).length) {
      setTimeout(() => document.querySelector("[data-testid=field-error]")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setStep((s) => s + 1);
    scrollTop();
  };
  const back = () => { setStep((s) => s - 1); scrollTop(); };

  const submit = async () => {
    const e = validate(2, f);
    setErr(e);
    if (Object.keys(e).length) return;
    setSending(true);
    setSubmitError("");
    try {
      const { phone_country, phone_number, ...rest } = f;
      const r = await api.post("/quotes/cheffe", { ...rest, phone: formatPhone(phone_country, phone_number), guests: Number(f.guests) });
      if (r.data?.ok && r.data.reference) { setReference(r.data.reference); scrollTop(); }
      else setSubmitError("Votre demande n'a pas pu être enregistrée. Merci de réessayer.");
    } catch (ex) {
      setSubmitError(formatApiError(ex.response?.data?.detail) || "Votre demande n'a pas pu être enregistrée. Merci de réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6EF] pb-24 pt-28 lg:pt-36" data-testid="devis-cheffe-page">
      <Seo title="Devis Cheffe à domicile — Afro Regal" description="Demandez votre devis pour un dîner privé avec une cheffe à domicile : dîner romantique, anniversaire, repas entre amis ou en famille, réception VIP." />
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="mb-10 text-center">
          <ChefHat size={28} strokeWidth={1.3} className="mx-auto text-[#C9A84C]" />
          <p className="mt-4 text-sm uppercase tracking-[0.3em] text-[#9A7B2C]">Devis cheffe à domicile</p>
          <h1 className="mt-3 font-serif text-4xl font-500 text-[#2C2C2C] sm:text-5xl">Organisons votre dîner privé</h1>
          {!reference && <p className="mx-auto mt-4 max-w-lg text-base text-[#2C2C2C]/65">Trois étapes, quelques minutes. Vous pouvez revenir en arrière à tout moment sans perdre vos réponses.</p>}
        </div>
        <div className="rounded-[28px] border border-[#C9A84C]/30 bg-white p-6 shadow-[0_30px_60px_-40px_rgba(44,44,44,0.35)] sm:p-10">
          {reference ? <Success reference={reference} /> : (
            <>
              <Stepper step={step} />
              <input type="text" name="website" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              {step === 0 && <StepContact f={f} set={set} err={err} />}
              {step === 1 && <StepProject f={f} set={set} err={err} toggleService={toggleService} />}
              {step === 2 && (
                <div className="space-y-6" data-testid="chef-step-3">
                  <p className="text-base text-[#2C2C2C]/75">Vérifiez votre demande avant de l'envoyer.</p>
                  <Summary f={f} />
                  <label className="flex items-start gap-3 text-sm text-[#2C2C2C]/75">
                    <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 h-4 w-4 accent-[#C9A84C]" data-testid="chef-consent" />
                    <span>J'accepte qu'Afro Regal utilise ces informations pour me recontacter au sujet de ma demande. <Link to="/confidentialite" className="underline">Politique de confidentialité</Link></span>
                  </label>
                  {err.consent && <p className="text-xs text-red-600" data-testid="field-error">{err.consent}</p>}
                  {submitError && <p className="rounded-xl bg-red-50 p-4 text-sm text-red-700" data-testid="chef-submit-error">{submitError}</p>}
                </div>
              )}
              <div className="mt-10 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                {step > 0 ? (
                  <button type="button" onClick={back} disabled={sending} data-testid="chef-back-btn" className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-[#2C2C2C] transition-colors hover:bg-[#FAF6EF]"><ArrowLeft size={16} /> Retour</button>
                ) : <span />}
                {step < 2 ? (
                  <button type="button" onClick={next} data-testid="chef-continue-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2C2C2C] px-8 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#C9A84C]">Continuer <ArrowRight size={16} /></button>
                ) : (
                  <button type="button" onClick={submit} disabled={sending} data-testid="chef-submit-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A84C] px-8 py-3.5 text-sm font-medium text-[#2C2C2C] transition-colors hover:bg-[#2C2C2C] hover:text-white disabled:opacity-60">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Envoyer ma demande de devis
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
